import * as z from 'zod';
import moment from 'moment';
import { PreferredSexLabel } from '@/marketplace-custom-config.ts';

/**
 * Coerce value into a number, optionally
 *
 * z.coerce.number().optional() doesn't quite do what we want
 * as it turns null into 0, and other things like that...
 */
export const coerceOptionalNumber = z.preprocess((val) => {
  if (/^\d+$/.test(String(val))) return Number(val);
  return undefined;
}, z.number().optional());

export const LatLngSchema = z.object({
  lat: z.number(),
  lng: z.number(),
});

export type LatLngData = z.infer<typeof LatLngSchema>;

export const BoundsSchema = z.object({
  ne: LatLngSchema,
  sw: LatLngSchema,
});

export const LocationSchema = z.object({
  address: z.string(), // formatted address with all address components

  street: z.string().optional(),
  street2: z.string().optional(),
  city: z.string().optional(),
  postCode: z.string().optional(),
  region: z.string().optional(), // e.g. county
  countryCode: z.string().optional(),

  origin: LatLngSchema.optional(),
  bounds: BoundsSchema.optional(),
});

export type LocationData = z.infer<typeof LocationSchema>;

export const DateObjectSchema = z.object({
  day: z.number(),
  month: z.number(),
  year: z.number(),
});

export const isoDateStringToBirthday = z.codec(z.iso.date(), DateObjectSchema, {
  // ISO string -> Date object
  decode: (isoString) => {
    const date = new Date(isoString);
    return {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
  },
  // Date object to ISO date string
  encode: (dateObj) => {
    const date = new Date(dateObj.year, dateObj.month - 1, dateObj.day);
    return moment(date).format('YYYY-MM-DD');
  },
});

export const UpdateAccountSchema = z
  .object({
    // Fields from the "account" pages
    jobTitle: z.string().optional(),
    addressDepartment: z.string().optional(),
    location: LocationSchema.optional(),
    gpCode: z.string().optional(),
    gpName: z.string().optional(),
    gpLocation: LocationSchema.optional(),
    nhsNumber: z.string().optional(),
    phoneNumber: z.string().optional(),
    birthday: DateObjectSchema.optional(),

    // Field from the favourites page, e.g. "Female", "No preference"
    preferredGender: z.string().optional(),

    // Fields from the "profile" page
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    myPronouns: z.string().optional(),
    userTypes: z.array(z.string()).optional(),
    // "to share what you are using this service for with other users."
    userTypeConsent: z.boolean().optional(),
    myLanguage: z.string().optional(),
    bio: z.string().optional(),
  })
  .strict();

export type UpdateAccountData = z.infer<typeof UpdateAccountSchema>;

export const bookingListTypes = ['by-you', 'for-you', 'your-work'] as const;

export type BookingListType = (typeof bookingListTypes)[number];

export const BookingListSchema = z.object({
  type: z.enum(bookingListTypes).optional(),
  limit: z.coerce.number().optional(),
});

export type BookingListParams = z.infer<typeof BookingListSchema>;

// Booking schemas

export const cancelledByOptions = ['customer', 'user'] as const;

export const CancelBookingSchema = z.object({
  cancelledBy: z.enum(cancelledByOptions),
  reason: z.string().optional(),
});

export type CancelBookingData = z.infer<typeof CancelBookingSchema>;

/** Customers can't cancel online a booking that starts within this many hours */
export const BOOKING_CANCEL_CUTOFF_HOURS = 24;

export function isWithinBookingCancelCutoff(start: string): boolean {
  return moment(start).isBefore(
    moment().add(BOOKING_CANCEL_CUTOFF_HOURS, 'hours')
  );
}

export const AmendBookingSchema = z.object({
  bookingStart: z.iso.datetime(),
  bookingEnd: z.iso.datetime(),
  // The booking description, shown to the professional (the old "session notes")
  description: z.string().optional(),
  // Only used when someone else is the deaf person, not the booker
  deafPersonName: z.string().optional(),
  deafPersonSMS: z.string().optional(),
});

export type AmendBookingData = z.infer<typeof AmendBookingSchema>;

/** Cancelling a booking that starts within this many days may incur a charge */
export const BOOKING_FREE_CANCEL_DAYS = 14;

export function isWithinFreeCancelWindow(start: string): boolean {
  return moment(start).isAfter(moment().add(BOOKING_FREE_CANCEL_DAYS, 'days'));
}

/** Customers can't edit a booking that starts within this many days */
export const BOOKING_CHANGE_CUTOFF_DAYS = 14;

export function isWithinBookingChangeCutoff(start: string): boolean {
  return moment(start).isBefore(
    moment().add(BOOKING_CHANGE_CUTOFF_DAYS, 'days')
  );
}

export const YourDetailsSchema = z.object({
  title: z.string().optional(),
  tel: z.string().optional(),
  telImport: z.boolean().optional(),
  invoicingAddress: LocationSchema.optional(),
  email: z.string().optional(),
  description: z.string().optional(),
  secondaryEmail: z.string().optional(),
  addressDepartment: z.string().optional(),

  // Not sure about these
  companyNameOrOrganization: z.string().optional(),
  contractCheck: z.string().optional(),
  contractDepartment: z.string().optional(),
});

export type YourDetailsData = z.infer<typeof YourDetailsSchema>;

export const DeafPersonDetailsSchema = z.object({
  bookingForSelf: z.boolean(),
  deafPersonName: z.string().optional(),
  deafPersonSMS: z.string().optional(),
  contractBookingTypeId: coerceOptionalNumber,
  communicationDomainId: coerceOptionalNumber,
  appointmentDoc: z.string().optional(),
  appointmentDocFileId: z.string().optional(),
});

export type DeafPersonDetailsData = z.infer<typeof DeafPersonDetailsSchema>;

export const BookingLocationSchema = z.object({
  personOrRemote: z.enum(['f2f', 'remote']),
  remoteDetails: z.string().optional(),
  isRecorded: z.enum(['yes', 'no', 'dont_know']).optional(),
  bookingStart: z.iso.datetime(),
  bookingEnd: z.iso.datetime(),

  // Phew these location things are complex/messy!
  // newBookingToLead has to deal with them later...
  location: LocationSchema.optional(),
  locationName: z.string().optional(),
  locationShort: z
    .object({ key: z.string(), value: z.string(), label: z.string() })
    .optional(),
  addressDepartment: z.string().optional(),
  bookingDepartment: z
    .object({ key: z.string(), label: z.string(), department: z.string() })
    .optional(),
  bookingDepartmentName: z.string().optional(),
  subLocation: z
    .object({ key: z.string(), value: z.string(), label: z.string() })
    .optional(),
  subLocationName: z.string().optional(),

  contact: z.string().optional(), // contact on arrival
});

export type BookingLocationData = z.infer<typeof BookingLocationSchema>;

export const InterpreterInfoSchema = z.object({
  value: z.coerce.number().nullable(),
  label: z.string(),
});

export type InterpreterInfo = z.infer<typeof InterpreterInfoSchema>;

const preferredSexLabels: PreferredSexLabel[] = [
  'No preference',
  'Female',
  'Male',
];

export const DetailsSchema = z.object({
  professionalTypeId: coerceOptionalNumber,
  preferredSex: z.enum(preferredSexLabels),
  preferredInterpreter: InterpreterInfoSchema.optional(),
  preferredInterpreter2: InterpreterInfoSchema.optional(),
  tos: z.boolean(),
  interpretersNeeded: coerceOptionalNumber,
});

export type DetailsData = z.infer<typeof DetailsSchema>;

export const NewBookingSchema = z.object({
  ...YourDetailsSchema.shape,
  ...DeafPersonDetailsSchema.shape,
  ...BookingLocationSchema.shape,
  ...DetailsSchema.shape,
});

export type NewBookingData = z.infer<typeof NewBookingSchema>;
